/**
 * Calendar-date arithmetic.
 *
 * An expiry is a CALENDAR DATE, not an instant. "Best before 1 Aug" is the same
 * day whether you are in Kolkata or in a plane over Dubai, so expiries are
 * stored as `YYYY-MM-DD` strings and never as epoch milliseconds. Storing them
 * as instants means an item silently changes its expiry day when the phone
 * crosses a timezone, or when the server (UTC) disagrees with the household.
 *
 * Instants — when something was purchased, when an event happened — stay epoch
 * millis, because those genuinely are moments in time.
 *
 * This also happens to match what extraction already returns
 * (`expiryDate: "2026-08-01"`), so nothing is converted twice.
 */
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;
export function isIsoDate(v) {
    return typeof v === "string" && ISO.test(v) && !Number.isNaN(toUtcNoon(v));
}
/**
 * Parses to UTC NOON, not midnight. Noon is far enough from either boundary
 * that no timezone offset or DST shift can push the date onto a neighbouring
 * day, which is exactly the bug this module exists to prevent.
 */
function toUtcNoon(iso) {
    const m = ISO.exec(iso);
    if (!m)
        return Number.NaN;
    const [, y, mo, d] = m;
    const t = Date.UTC(Number(y), Number(mo) - 1, Number(d), 12);
    // Rejects 2026-02-31 and friends, which Date.UTC would happily roll over.
    const back = new Date(t);
    if (back.getUTCMonth() !== Number(mo) - 1 || back.getUTCDate() !== Number(d)) {
        return Number.NaN;
    }
    return t;
}
const pad = (n) => String(n).padStart(2, "0");
/** Today in a specific household's zone — not the server's, and not UTC. */
export function todayIso(timezone, now = Date.now()) {
    try {
        // en-CA formats as YYYY-MM-DD, which is the whole reason it is used here.
        return new Intl.DateTimeFormat("en-CA", {
            timeZone: timezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).format(new Date(now));
    }
    catch {
        const d = new Date(now);
        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
    }
}
/**
 * The hour of the day, 0–23, in a specific household's zone.
 *
 * Exists so the reminder sender can fire at a civilised local hour rather than
 * a civilised hour where the datacentre happens to be. The scheduler wakes
 * hourly and every household is asked "is it your turn yet?" — which is the
 * only approach that works once two households are in different zones, since
 * there is no single UTC instant that is 9am for both.
 *
 * Falls back to UTC on an unrecognised zone, matching `todayIso`: a reminder at
 * an odd hour is a nuisance, a thrown error in a scheduled job is silence.
 */
export function hourIn(timezone, now = Date.now()) {
    try {
        // `hourCycle: "h23"` matters: the default for en-US is h12, which formats
        // midnight as "24" and would put the check squarely off by a day.
        const h = new Intl.DateTimeFormat("en-GB", {
            timeZone: timezone,
            hour: "2-digit",
            hourCycle: "h23",
        }).format(new Date(now));
        const n = Number.parseInt(h, 10);
        return Number.isNaN(n) ? new Date(now).getUTCHours() : n;
    }
    catch {
        return new Date(now).getUTCHours();
    }
}
export function addDays(iso, days) {
    const t = toUtcNoon(iso) + days * DAY_MS;
    const d = new Date(t);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
/** Whole days from `from` to `to`. Negative when `to` is in the past. */
export function daysBetween(from, to) {
    return Math.round((toUtcNoon(to) - toUtcNoon(from)) / DAY_MS);
}
/**
 * How far through a span a date is, as 0-1.
 *
 * Drives the item timeline's progress line: solid for the shelf life already
 * spent, dotted for what is left. Clamped at both ends, because the two states
 * outside the range are real and neither is an error — an item added today has
 * used none of its life, and one a week past its date has used all of it and
 * then some. A bar that overflowed its track would be reporting the overshoot
 * as a rendering bug.
 *
 * A zero-length or backwards span is 1, not a division by zero: something whose
 * date arrives the day it was bought has no shelf life left to draw.
 */
export function fractionElapsed(from, to, at) {
    const span = daysBetween(from, to);
    if (span <= 0)
        return 1;
    const gone = daysBetween(from, at);
    if (gone <= 0)
        return 0;
    if (gone >= span)
        return 1;
    return gone / span;
}
export function isoFromMillis(ms, timezone) {
    return todayIso(timezone, ms);
}
/** Start-of-day instant, for scheduling. */
export function millisFromIso(iso) {
    return toUtcNoon(iso);
}
export function compareIso(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/** 0 = Sunday. Sunday-first because that is how Indian calendars read. */
export function weekday(iso) {
    return new Date(toUtcNoon(iso)).getUTCDay();
}
/** The Sunday on or before `iso`. Used to align a calendar grid to real weeks. */
export function startOfWeek(iso) {
    return addDays(iso, -weekday(iso));
}
export const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];
/** "Aug", for a month label above the grid. */
export function monthLabel(iso) {
    const m = ISO.exec(iso);
    return m ? (MONTHS[Number(m[2]) - 1] ?? "") : "";
}
export function dayOfMonth(iso) {
    const m = ISO.exec(iso);
    return m ? Number(m[3]) : 0;
}
/**
 * How an expiry reads to a human. The whole product is a nagging feeling about
 * time, so the wording carries real weight — "in 2 days" prompts action in a way
 * that "2026-08-11" never does.
 */
export function describeDaysLeft(days) {
    if (days === 0)
        return "today";
    if (days === 1)
        return "tomorrow";
    if (days === -1)
        return "yesterday";
    if (days > 0)
        return `in ${days} days`;
    return `${Math.abs(days)} days ago`;
}
/**
 * Relative-date chips for manual entry.
 *
 * Most people do not know the exact date, but everyone knows "about a week".
 * Forcing a calendar picker for a guess is what makes manual entry feel slow —
 * and manual entry is the floor that always works, so it has to be the fastest
 * path, not the fallback (docs/product/intake.md).
 */
export const QUICK_EXPIRY = [
    { label: "2 days", days: 2 },
    { label: "5 days", days: 5 },
    { label: "1 week", days: 7 },
    { label: "2 weeks", days: 14 },
    { label: "1 month", days: 30 },
    { label: "3 months", days: 90 },
];
const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
/**
 * Renders only as precisely as the source justifies. A label that printed
 * `03/2028` must never be shown as "1 Mar 2028" — the day was never on the pack
 * and inventing it is the same mistake the extraction validator exists to catch.
 */
export function formatIsoDate(iso, precision = "day") {
    const m = ISO.exec(iso);
    if (!m)
        return iso;
    const [, y = "", mo = "", d = ""] = m;
    const month = MONTHS[Number(mo) - 1] ?? mo;
    if (precision === "year")
        return y;
    if (precision === "month")
        return `${month} ${y}`;
    return `${Number(d)} ${month} ${y}`;
}
//# sourceMappingURL=dates.js.map